// src/pages/GamesPage.js

import React, { useState, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import GameAllGrid from "../components/games/GameAllGrid";
import { useLocation } from "react-router-dom";

const FILTER_OPTIONS = {
  gameJam: [
    { key: "게임잼",      label: "게임잼 All" },
    { key: "게임잼 2023", label: "게임잼 2023" },
    { key: "게임잼 2024", label: "게임잼 2024" },
    { key: "게임잼 2025", label: "게임잼 2025" },
  ],
  genre: [
    { key: "어드벤처",    label: "어드벤처" },
    { key: "액션",       label: "액션" },
    { key: "캐주얼",     label: "캐주얼" },
    { key: "비주얼 노벨", label: "비주얼 노벨" },
    { key: "시뮬레이션",  label: "시뮬레이션" },
    { key: "RPG",       label: "RPG" },
    { key: "퍼즐",      label: "퍼즐" },
  ],
  rating: [
    { key: "전체 이용가",    label: "전체 이용가" },
    { key: "12세 이용가",    label: "12세 이용가" },
    { key: "15세 이용가",    label: "15세 이용가" },
    { key: "청소년이용불가",  label: "청소년이용불가" },
  ],
  language: [
    { key: "한국어",    label: "한국어" },
    { key: "영어",      label: "영어" },
    { key: "중국어(번체)", label: "중문(번체)" },
    { key: "중국어(간체)", label: "중문(간체)" },
    { key: "일본어",    label: "일본어" },
  ],
};

export default function GamesPage() {
  const { state } = useLocation();
  const [allGames, setAllGames] = useState([]);
  const [sortField, setSortField] = useState("title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(200000);
  const [tempMin, setTempMin] = useState(0);
  const [tempMax, setTempMax] = useState(200000);
  const [discountOnly, setDiscountOnly] = useState(false);
  const [hideFree, setHideFree] = useState(false);
  const [hideComingSoon, setHideComingSoon] = useState(false);
  const [showOnlyComingSoon, setShowOnlyComingSoon] = useState(false);

  const [selected, setSelected] = useState({
    gameJam:   new Set(),
    genre:     new Set(),
    rating:    new Set(),
    language:  new Set(),
  });
  const [showMoreGenre, setShowMoreGenre] = useState(false);
  const [resultCount, setResultCount] = useState(0);

  // URL state → 출시예정 토글
  useEffect(() => {
    if (state?.showOnlyComingSoon) {
      setShowOnlyComingSoon(true);
    }
  }, [state?.showOnlyComingSoon]);

  // URL state → 특정 카테고리 필터 활성화
  useEffect(() => {
    if (state?.filterCategory && state?.filterValue) {
      setSelected(prev => {
        const next = { ...prev };
        next[state.filterCategory] = new Set([ state.filterValue ]);
        return next;
      });
    }
  }, [state?.filterCategory, state?.filterValue]);

  // Firestore 'games' 구독
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "games"), snap => {
      setAllGames(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // 필터 토글: gameJam All/Year 상호배제 처리 (All을 다시 눌러 해제 가능)
  const toggleFilter = (cat, key) => {
    setSelected(sel => {
      const next = { ...sel, [cat]: new Set(sel[cat]) };

      if (cat === "gameJam") {
        if (key === "게임잼") {
          if (next.gameJam.has("게임잼")) next.gameJam.delete("게임잼");
          else {
            next.gameJam.clear();
            next.gameJam.add("게임잼");
          }
        } else {
          if (next.gameJam.has(key)) next.gameJam.delete(key);
          else next.gameJam.add(key);
          next.gameJam.delete("게임잼");
        }
      } else {
        if (next[cat].has(key)) next[cat].delete(key);
        else next[cat].add(key);
      }
      return next;
    });
  };

  const renderGenrePills = () => {
    const opts = FILTER_OPTIONS.genre;
    const list = showMoreGenre ? opts : opts.slice(0, 5);

    return (
      <>
        <div className="flex flex-wrap gap-2">
          {list.map(o => {
            const count = allGames.filter(game =>
              Array.isArray(game.genres)
                ? game.genres.includes(o.key)
                : String(game.genre) === o.key
            ).length;

            return (
              <button
                key={o.key}
                onClick={() => toggleFilter("genre", o.key)}
                className={`px-3 py-1 text-sm rounded-full border transition ${
                  selected.genre.has(o.key)
                    ? "bg-neutral-600 text-white border-neutral-500"
                    : "bg-neutral-700 text-neutral-300 border-neutral-600"
                }`}
              >
                {o.label}
                <span className="ml-1 text-xs text-neutral-400">({count})</span>
              </button>
            );
          })}
        </div>
        {opts.length > 5 && (
          <button
            onClick={() => setShowMoreGenre(!showMoreGenre)}
            className="mt-1 text-xs text-neutral-400 hover:underline"
          >
            {showMoreGenre ? "접기" : "더보기+"}
          </button>
        )}
      </>
    );
  };

  const renderPills = category => (
    <div className="flex flex-wrap gap-2">
      {FILTER_OPTIONS[category].map(o => {
        let count = 0;

        if (category === "gameJam") {
          count = allGames.filter(game =>
            Array.isArray(game.tags) &&
            game.tags.some(t => t.includes(o.key))
          ).length;

        } else if (category === "language") {
          count = allGames.filter(game =>
            Array.isArray(game.languages) &&
            game.languages.some(
              langObj =>
                langObj.name === o.key &&
                (langObj.audio || langObj.subtitle || langObj.ui)
            )
          ).length;

        } else if (category === "rating") {
          count = allGames.filter(game =>
            String(game.rating) === o.key
          ).length;

        } else {
          // 기타 카테고리 (필요 시 확장)
          count = allGames.filter(game =>
            Array.isArray(game[category]) &&
            game[category].includes(o.key)
          ).length;
        }

        return (
          <button
            key={o.key}
            onClick={() => toggleFilter(category, o.key)}
            className={`px-3 py-1 text-sm rounded-full border transition ${
              selected[category].has(o.key)
                ? "bg-neutral-600 text-white border-neutral-500"
                : "bg-neutral-700 text-neutral-300 border-neutral-600"
            }`}
          >
            {o.label}
            <span className="ml-1 text-xs text-neutral-400">({count})</span>
          </button>
        );
      })}
    </div>
  );

  // 활성 필터 수집
  const activeFilters = [];
  if (searchTerm)                activeFilters.push({ key: "search",          label: searchTerm });
  if (priceMin > 0 || priceMax < 200000)
                                 activeFilters.push({ key: "price",           label: `${priceMin}~${priceMax}` });
  if (discountOnly)              activeFilters.push({ key: "discount",        label: "할인중" });
  if (hideFree)                  activeFilters.push({ key: "hideFree",        label: "무료숨김" });
  if (hideComingSoon)            activeFilters.push({ key: "comingSoon",      label: "출시예정 숨김" });
  if (showOnlyComingSoon)        activeFilters.push({ key: "onlyComingSoon",  label: "출시예정만 보기" });
  Object.entries(selected).forEach(([cat, set]) =>
    set.forEach(key =>
      activeFilters.push({
        key: `${cat}:${key}`,
        label: FILTER_OPTIONS[cat].find(o => o.key === key)?.label
      })
    )
  );

  const clearAll = () => {
    setSearchTerm("");
    setPriceMin(0); setPriceMax(200000);
    setTempMin(0);  setTempMax(200000);
    setDiscountOnly(false); setHideFree(false);
    setHideComingSoon(false); setShowOnlyComingSoon(false);
    setSelected({
      gameJam:   new Set(),
      genre:     new Set(),
      rating:    new Set(),
      language:  new Set(),
    });
  };

  const removeFilter = key => {
    if (key === "search")            setSearchTerm("");
    else if (key === "price") {
      setPriceMin(0); setPriceMax(200000);
      setTempMin(0);  setTempMax(200000);
    }
    else if (key === "discount")     setDiscountOnly(false);
    else if (key === "hideFree")     setHideFree(false);
    else if (key === "comingSoon")   setHideComingSoon(false);
    else if (key === "onlyComingSoon") setShowOnlyComingSoon(false);
    else {
      const [cat, k] = key.split(":");
      if (cat && k) {
        setSelected(sel => {
          const next = { ...sel, [cat]: new Set(sel[cat]) };
          next[cat].delete(k);
          return next;
        });
      }
    }
  };

  return (
    <div className="pt-6 bg-[#121212] min-h-screen">
      <div className="container mx-auto px-6 mb-4">
        <h1 className="text-2xl font-bold text-white">전체 게임</h1>
      </div>
      <div className="container mx-auto px-6 grid lg:grid-cols-[3fr_1fr] gap-6 items-start">
        <div>
          {/* 정렬 & 검색 결과 수 */}
          <div className="mb-4 flex justify-between items-center">
            <span className="text-sm text-neutral-400">
              검색 결과 {activeFilters.length ? resultCount : allGames.length}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-white">정렬:</span>
              <select
                value={sortField}
                onChange={e => setSortField(e.target.value)}
                className="bg-neutral-800 text-white rounded px-2 py-1 text-sm"
              >
                <option value="title">이름순</option>
                <option value="price">가격순</option>
              </select>
              <select
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                className="bg-neutral-800 text-white rounded px-2 py-1 text-sm"
              >
                <option value="asc">▲</option>
                <option value="desc">▼</option>
              </select>
            </div>
          </div>

          {/* 액티브 필터 */}
          {activeFilters.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {activeFilters.length > 1 && (
                <button
                  onClick={clearAll}
                  className="px-2 py-1 bg-neutral-600 text-white rounded text-sm"
                >
                  전체 해제
                </button>
              )}
              {activeFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => removeFilter(f.key)}
                  className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded text-sm"
                >
                  {f.label} <span>×</span>
                </button>
              ))}
            </div>
          )}

          <GameAllGrid
            sortField={sortField}
            sortOrder={sortOrder}
            searchTerm={searchTerm}
            priceRange={[priceMin, priceMax]}
            discountOnly={discountOnly}
            hideFree={hideFree}
            excludeComingSoon={!showOnlyComingSoon && hideComingSoon}
            showOnlyComingSoon={showOnlyComingSoon}
            filters={selected}
            onCount={setResultCount}
          />
        </div>

        <aside className="bg-neutral-800 p-6 rounded-lg space-y-6 text-white">
          {/* 검색 박스 */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-neutral-700" />
            <input
              type="text"
              placeholder="검색하기"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-neutral-700 placeholder-neutral-400 rounded-full py-2 pl-10 pr-4 text-sm focus:outline-none"
            />
          </div>

          <hr className="border-t border-neutral-600" />

          {/* 가격 필터 */}
          <div>
            <h3 className="text-base font-semibold mb-2">상품 가격</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="w-1/2 bg-neutral-700 rounded px-2 py-1 text-sm"
                value={tempMin}
                onChange={e => setTempMin(+e.target.value)}
                onBlur={() => setPriceMin(tempMin)}
                min={0}
              />
              <span className="text-sm">~</span>
              <input
                type="number"
                className="w-1/2 bg-neutral-700 rounded px-2 py-1 text-sm"
                value={tempMax}
                onChange={e => setTempMax(+e.target.value)}
                onBlur={() => setPriceMax(tempMax)}
                min={0}
              />
            </div>
          </div>

          <div className="space-y-2">
            {[
              { state: discountOnly,       setter: setDiscountOnly,       label: "할인 중인 상품만 보기" },
              { state: hideFree,           setter: setHideFree,           label: "무료 상품 숨기기" },
              { state: hideComingSoon,     setter: setHideComingSoon,     label: "출시 예정 숨기기" },
              { state: showOnlyComingSoon, setter: setShowOnlyComingSoon, label: "출시 예정만 보기" },
            ].map(({ state, setter, label }) => (
              <label key={label} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={state}
                  onChange={e => setter(e.target.checked)}
                  className="form-checkbox"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>

          <hr className="border-t border-neutral-600" />

          <div>
            <h3 className="text-base font-semibold mb-2">게임잼</h3>
            {renderPills("gameJam")}
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">장르</h3>
            {renderGenrePills()}
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">이용 등급</h3>
            {renderPills("rating")}
          </div>

          <div>
            <h3 className="text-base font-semibold mb-2">지원 언어</h3>
            {renderPills("language")}
          </div>
        </aside>
      </div>
    </div>
  );
}
